import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormField } from '../components/FormField';
import { Button } from '../components/Button';
import { useKidApi } from '../api/kid';
import { ApiError } from '../api/client';
import { isValidPastDob } from '../lib/age';
import { compressForUpload } from '../lib/image-resize';
import { PlanCapReached } from '../components/PlanCapReached';

// Photo constraints mirror KidPhotoUploader so the create + edit paths
// validate identically client-side. Server still enforces independently.
const PHOTO_ALLOWED = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);
const PHOTO_MAX_BYTES = 10 * 1024 * 1024;

export function KidAdd() {
  const navigate = useNavigate();
  const api = useKidApi();
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  // 2026-05-19: Position + kit colour removed from the kid profile UI.
  // Position lives per-match (MatchAdd / MatchRecord); kit colour was a
  // decoration that didn't earn its place in the create form. The Kid
  // schema still carries both fields for back-compat with existing data;
  // they're just not exposed to new accounts here.
  const [defaultTeamName, setDefaultTeamName] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  // F1: 2nd-kid cap-hit → full-screen celebration screen.
  const [capHit, setCapHit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Photo is picked client-side and held as a File. We don't upload it
  // until AFTER the kid record is created (the S3 key needs a kidId).
  const photoFileRef = useRef<HTMLInputElement>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);

  // Local-only blob URL for the preview circle. Revoke on change/unmount
  // so we don't leak object URLs (Safari is strict about this).
  useEffect(() => {
    if (!photoFile) { setPhotoPreviewUrl(null); return; }
    const url = URL.createObjectURL(photoFile);
    setPhotoPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  const choosePhoto = () => photoFileRef.current?.click();

  const onPhotoPicked = (file: File) => {
    setPhotoError(null);
    if (!PHOTO_ALLOWED.has(file.type)) {
      setPhotoError('JPEG, PNG, WebP or HEIC.');
      return;
    }
    if (file.size > PHOTO_MAX_BYTES) {
      setPhotoError(`Under ${Math.round(PHOTO_MAX_BYTES / 1024 / 1024)}MB.`);
      return;
    }
    setPhotoFile(file);
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoError(null);
    if (photoFileRef.current) photoFileRef.current.value = '';
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = "Your child's first name.";
    if (!dob) next.dob = "Date of birth (YYYY-MM-DD).";
    else if (!isValidPastDob(dob)) next.dob = "Pick a date in the past, within the last 25 years.";
    setErrors(next);
    if (Object.keys(next).length) return;

    setSubmitting(true);
    try {
      const created = await api.create({
        name: name.trim(),
        dob,
        defaultTeamName: defaultTeamName.trim() || undefined,
      });
      // Second-stage photo upload — same 3-step flow as KidPhotoUploader
      // (presign → S3 POST → PATCH photoPath). The kid record is already
      // saved by this point, so a failed photo upload doesn't lose the
      // child; we surface the error and still navigate.
      if (photoFile) {
        try {
          const compressed = await compressForUpload(photoFile);
          const presign = await api.presignPhoto(created.kidId, compressed.type, compressed.size);
          const fd = new FormData();
          for (const [k, v] of Object.entries(presign.fields)) fd.append(k, v);
          fd.append('file', compressed);
          const r = await fetch(presign.uploadUrl, { method: 'POST', body: fd });
          if (!r.ok) throw new Error(`Upload failed (HTTP ${r.status})`);
          await api.update(created.kidId, { photoPath: presign.key });
        } catch (uploadErr) {
          // Non-fatal — kid exists, photo can be added on the edit screen.
          console.warn('[kid-add] photo upload failed; kid saved without photo:', uploadErr);
        }
      }
      // Land on the new kid's page so the photo (if uploaded) is visible
      // and the next step ("Record a match") is one tap away.
      navigate(`/kids/${encodeURIComponent(created.kidId)}`);
    } catch (err) {
      const e2 = err as ApiError;
      if (e2.status === 403 && e2.code === 'plan_limit_reached') {
        setCapHit(true);
      } else {
        setErrors(e2.fields ?? { form: e2.message || 'Could not add. Try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    {capHit ? <PlanCapReached cap="kids" onDismiss={() => setCapHit(false)} /> : null}
    <form onSubmit={submit}>
      <h1 style={{ marginBottom: 8 }}>Add a child</h1>
      <p style={{ color: 'var(--muted)', marginBottom: 24 }}>
        Just the basics. You can fill in the rest later.
      </p>

      {/* Photo picker — chosen client-side; uploaded after the kid record
          is created (the S3 key needs a kidId). Mirrors the KidEdit
          uploader's allowlist + size cap. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-base)',
          padding: 'var(--space-base)',
          background: 'var(--surface-2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 16,
        }}
      >
        <div
          style={{
            width: 80, height: 80,
            borderRadius: '50%',
            overflow: 'hidden',
            background: photoPreviewUrl ? '#000' : 'var(--green-light)',
            display: 'grid', placeItems: 'center',
            flexShrink: 0,
            boxShadow: 'var(--shadow-1)',
          }}
          aria-hidden
        >
          {photoPreviewUrl ? (
            <img src={photoPreviewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <span style={{ fontSize: '2rem' }}>📷</span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 'var(--small)', color: 'var(--muted)', marginBottom: 8 }}>
            {photoFile ? `Photo ready: ${photoFile.name}` : 'A photo brings the journal to life. Optional.'}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              ref={photoFileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onPhotoPicked(f); }}
            />
            {/* type="button" is critical — without it, this button inside
                the surrounding <form> would submit the form (caused the
                2026-05-18 kid-photo bug; AGENTS.md gotcha #7). The Button
                component now defaults type to "button" but being explicit
                here is cheap insurance. */}
            <Button
              label={photoFile ? 'Replace photo' : 'Add a photo'}
              onClick={choosePhoto}
              variant="ghost"
              type="button"
            />
            {photoFile ? (
              <Button
                label="Remove"
                onClick={removePhoto}
                variant="ghost"
                type="button"
                style={{ color: 'var(--electric-red)' }}
              />
            ) : null}
          </div>
          {photoError ? (
            <div style={{ color: 'var(--electric-red)', fontSize: 'var(--small)', marginTop: 6 }}>
              {photoError}
            </div>
          ) : null}
        </div>
      </div>

      <FormField
        label="First name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoComplete="off"
        placeholder="Sam"
        error={errors.name}
        required
      />
      <FormField
        label="Date of birth"
        type="date"
        value={dob}
        onChange={(e) => setDob(e.target.value)}
        max={new Date().toISOString().slice(0, 10)}
        error={errors.dob}
        required
      />

      <FormField
        label="Team (optional)"
        value={defaultTeamName}
        onChange={(e) => setDefaultTeamName(e.target.value)}
        autoComplete="off"
        placeholder="Girton U9s"
        hint="Position is set per match later — you can swap depending on the day."
      />

      {errors.form ? <p style={{ color: 'var(--error)', fontSize: 14 }}>{errors.form}</p> : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
        <Button label="Add child" type="submit" loading={submitting} />
        <button
          type="button"
          onClick={() => navigate('/kids')}
          style={{ background: 'transparent', border: 'none', color: 'var(--muted)', fontSize: 14, cursor: 'pointer', padding: 8 }}
        >
          Cancel
        </button>
      </div>
    </form>
    </>
  );
}
